import React, { useCallback, useRef, useState } from 'react';
import { Minus, Plus, Play, Loader2, X } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { SLOT_SCREEN_ORDER } from '@core/types/slot-screen-generator.types';
import type { VideoResolutionPreset } from '@core/types/slot-screen-generator.types';
import { VIDEO_DURATION_RANGE } from '../../constants/slot-screen-defaults';
import { generateAIVideo } from '../../services/slot-screen-generator/VideoCompilerService';
import { saveVideoToProject } from '../../services/slot-screen-generator';

const RESOLUTION_OPTIONS: VideoResolutionPreset[] = ['16:9', '9:16', 'custom'];

export function VideoControlBar() {
  const videoConfig = useSlotScreenGeneratorStore(s => s.videoConfig);
  const videoState = useSlotScreenGeneratorStore(s => s.videoState);
  const videoSlotImages = useSlotScreenGeneratorStore(s => s.videoSlotImages);
  const setVideoTotalDuration = useSlotScreenGeneratorStore(s => s.setVideoTotalDuration);
  const setVideoResolutionPreset = useSlotScreenGeneratorStore(s => s.setVideoResolutionPreset);
  const setVideoConfig = useSlotScreenGeneratorStore(s => s.setVideoConfig);
  const setVideoState = useSlotScreenGeneratorStore(s => s.setVideoState);
  const resetVideo = useSlotScreenGeneratorStore(s => s.resetVideo);
  const addVideoHistoryEntry = useSlotScreenGeneratorStore(s => s.addVideoHistoryEntry);
  const updateVideoHistoryEntry = useSlotScreenGeneratorStore(s => s.updateVideoHistoryEntry);

  const abortRef = useRef<AbortController | null>(null);
  const [customWidth, setCustomWidth] = useState(String(videoConfig.width));
  const [customHeight, setCustomHeight] = useState(String(videoConfig.height));

  const isCompiling = videoState.status === 'compiling';

  const filledSlots = SLOT_SCREEN_ORDER.filter(id => videoSlotImages[id]?.imageUrl).length;
  const filledPrompts = SLOT_SCREEN_ORDER.filter(id => videoSlotImages[id]?.animationPrompt?.trim()).length;
  const totalSlots = SLOT_SCREEN_ORDER.length;
  const readySlots = SLOT_SCREEN_ORDER.filter(id =>
    videoSlotImages[id]?.imageUrl && videoSlotImages[id]?.animationPrompt?.trim()
  ).length;
  const allReady = readySlots === totalSlots;

  const handleDurationStep = useCallback((delta: number) => {
    setVideoTotalDuration(videoConfig.totalDuration + delta);
  }, [videoConfig.totalDuration, setVideoTotalDuration]);

  const handleResolutionClick = useCallback((preset: VideoResolutionPreset) => {
    setVideoResolutionPreset(preset);
  }, [setVideoResolutionPreset]);

  const handleCustomDims = useCallback(() => {
    const w = parseInt(customWidth) || videoConfig.width;
    const h = parseInt(customHeight) || videoConfig.height;
    setVideoConfig({ width: w, height: h });
  }, [customWidth, customHeight, videoConfig, setVideoConfig]);

  const handleGenerate = useCallback(async (mode: 'full' | 'part') => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // For partial mode, filter to only ready slots (have both image + animation prompt)
    const slotsToUse = mode === 'full'
      ? videoSlotImages
      : Object.fromEntries(
          SLOT_SCREEN_ORDER
            .filter(id => videoSlotImages[id]?.imageUrl && videoSlotImages[id]?.animationPrompt?.trim())
            .map(id => [id, videoSlotImages[id]])
        ) as typeof videoSlotImages;

    const entryId = Math.random().toString(36).substring(2, 11);
    addVideoHistoryEntry({
      id: entryId,
      thumbnailUrl: null,
      videoUrl: '',
      savedPath: '',
      duration: videoConfig.totalDuration,
      resolution: { width: videoConfig.width, height: videoConfig.height },
      timestamp: Date.now(),
      status: 'generating',
    });

    setVideoState({ status: 'compiling', progress: 0, outputUrl: null, error: null, currentGenerationId: entryId });

    try {
      const blob = await generateAIVideo(
        slotsToUse,
        videoConfig,
        (pct) => setVideoState({ progress: pct }),
        controller.signal,
        (_screenId, status) => setVideoState({ currentScreenStatus: status }),
      );
      const url = URL.createObjectURL(blob);
      setVideoState({ status: 'done', progress: 100, outputUrl: url, currentGenerationId: null, currentScreenStatus: null });
      updateVideoHistoryEntry(entryId, { status: 'ready', videoUrl: url });
      // Save to project folder (fire-and-forget)
      saveVideoToProject(entryId, blob)
        .then((savedPath) => {
          if (savedPath) {
            useSlotScreenGeneratorStore.getState().updateVideoSavedPath(entryId, savedPath);
          }
        })
        .catch(() => {});
    } catch (err) {
      if (!controller.signal.aborted) {
        const errMsg = err instanceof Error ? err.message : String(err);
        setVideoState({ status: 'error', error: errMsg, currentGenerationId: null, currentScreenStatus: null });
        updateVideoHistoryEntry(entryId, { status: 'error', error: errMsg });
      } else {
        resetVideo();
        updateVideoHistoryEntry(entryId, { status: 'error', error: 'Cancelled' });
      }
    }
  }, [videoSlotImages, videoConfig, setVideoState, resetVideo, addVideoHistoryEntry, updateVideoHistoryEntry]);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    resetVideo();
  }, [resetVideo]);

  return (
    <div className="flex-shrink-0 border-t border-zinc-700 bg-zinc-900/95">
      {/* Progress bar */}
      {isCompiling && (
        <>
          <div className="h-0.5 bg-zinc-800">
            <div
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{ width: `${videoState.progress}%` }}
            />
          </div>
          {videoState.currentScreenStatus && (
            <div className="px-3 py-1 bg-zinc-800/50">
              <span className="text-[11px] text-zinc-400 truncate">{videoState.currentScreenStatus}</span>
            </div>
          )}
        </>
      )}

      <div className="flex items-center gap-4 px-2 py-2 flex-wrap">
        {/* Duration stepper */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-zinc-500 whitespace-nowrap">Duration</span>
          <button
            onClick={() => handleDurationStep(-VIDEO_DURATION_RANGE.step)}
            disabled={videoConfig.totalDuration <= VIDEO_DURATION_RANGE.min}
            className="w-5 h-5 rounded flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 transition-colors"
          >
            <Minus size={10} className="text-zinc-300" />
          </button>
          <span className="text-xs text-zinc-200 font-medium tabular-nums w-8 text-center">
            {videoConfig.totalDuration}s
          </span>
          <button
            onClick={() => handleDurationStep(VIDEO_DURATION_RANGE.step)}
            disabled={videoConfig.totalDuration >= VIDEO_DURATION_RANGE.max}
            className="w-5 h-5 rounded flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 transition-colors"
          >
            <Plus size={10} className="text-zinc-300" />
          </button>
        </div>

        {/* Resolution selector */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-zinc-500">Res</span>
          <div className="flex rounded overflow-hidden border border-zinc-700">
            {RESOLUTION_OPTIONS.map(preset => (
              <button
                key={preset}
                onClick={() => handleResolutionClick(preset)}
                className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                  videoConfig.resolutionPreset === preset
                    ? 'bg-zinc-600 text-zinc-100'
                    : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
          {/* Custom dimension inputs */}
          {videoConfig.resolutionPreset === 'custom' && (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={customWidth}
                onChange={e => setCustomWidth(e.target.value)}
                onBlur={handleCustomDims}
                className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-300 text-center tabular-nums focus:outline-none focus:border-zinc-500"
              />
              <span className="text-xs text-zinc-600">×</span>
              <input
                type="number"
                value={customHeight}
                onChange={e => setCustomHeight(e.target.value)}
                onBlur={handleCustomDims}
                className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-300 text-center tabular-nums focus:outline-none focus:border-zinc-500"
              />
            </div>
          )}
          {videoConfig.resolutionPreset !== 'custom' && (
            <span className="text-xs text-zinc-600 tabular-nums">
              {videoConfig.width}×{videoConfig.height}
            </span>
          )}
        </div>

        {/* Slot count indicator */}
        <span className={`text-xs whitespace-nowrap ${allReady ? 'text-green-400' : 'text-zinc-500'}`}>
          {filledSlots}/{totalSlots} images · {filledPrompts}/{totalSlots} prompts
        </span>

        <div className="flex-1" />

        {/* Error message */}
        {videoState.status === 'error' && videoState.error && (
          <span className="text-xs text-red-400 truncate max-w-[200px]" title={videoState.error}>
            {videoState.error}
          </span>
        )}

        {/* Cancel */}
        {isCompiling && (
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 px-2.5 py-1 text-xs text-zinc-300 border border-zinc-600 rounded hover:bg-zinc-800 transition-colors"
          >
            <X size={11} />
            Cancel
          </button>
        )}

        {/* Generate Part button (partial video from ready screens) */}
        <button
          onClick={() => handleGenerate('part')}
          disabled={isCompiling || readySlots === 0}
          title={readySlots === 0 ? 'Need at least 1 screen with image + animation prompt' : `Generate video from ${readySlots} ready screen(s)`}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-600 text-zinc-200 hover:bg-zinc-700 disabled:text-zinc-600 disabled:border-zinc-700 disabled:cursor-not-allowed font-medium text-xs rounded transition-colors"
        >
          <Play size={12} />
          Generate Part ({readySlots}/{totalSlots})
        </button>

        {/* Generate Video button (requires all 6 screens) */}
        <button
          onClick={() => handleGenerate('full')}
          disabled={isCompiling || !allReady}
          title={!allReady ? `Need all ${totalSlots} images and ${totalSlots} animation prompts` : 'Generate full video from all screens'}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-600/40 disabled:cursor-not-allowed text-black font-semibold text-xs rounded transition-colors"
        >
          {isCompiling ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Generating... {videoState.progress}%
            </>
          ) : (
            <>
              <Play size={12} />
              Generate Video
            </>
          )}
        </button>
      </div>
    </div>
  );
}
